import { configSchema, type Config } from './schema';

/**
 * Client-safe configuration loader
 * This module reads configuration from Next.js environment variables
 * and can be safely imported in both server and client contexts
 */

/**
 * Load configuration from Next.js environment variables
 * These are set by the server-side config loader in next.config.ts
 */
function loadClientConfig(): Config {
  try {
    // Build configuration object from Next.js environment variables
    const config: Config = {
      // App Configuration
      nodeEnv: (process.env.NODE_ENV as Config['nodeEnv']) || 'development',
      appVersion: process.env.NEXT_PUBLIC_APP_VERSION || 'dev',
      appEnv: (process.env.APP_ENV as Config['appEnv']) || 'local',

      // Protocol Configuration
      cbProtocol: (process.env.CB_PROTOCOL as Config['cbProtocol']) || 'https',
      disableApp: process.env.DISABLE_APP === 'true',

      // Domain and API Configuration
      cbDomain: process.env.CB_DOMAIN || 'devcb.in',
      cbAiApiUrl:
        process.env.CB_AI_API_URL || 'http://public.ai.localcb.in:9000',
      beehiveUrl: process.env.BEEHIVE_URL || 'https://apibeehive.devcb.in',
      enableEditCodeSamples: process.env.ENABLE_EDIT_CODE_SAMPLES === 'true',
      apiDocsBaseUrl:
        process.env.API_DOCS_BASE_URL ||
        'https://apidocs.chargebee.com/docs/api',

      // OpenAPI & Code Samples Configuration
      openApiSpecUrl: process.env.OPEN_API_SPEC_URL || '/pcv2.json',
      samplesDirectoryPath: process.env.SAMPLES_DIRECTORY_PATH || '/samples',
      codeSamplesBaseDirName:
        process.env.CODE_SAMPLES_BASE_DIR_NAME || 'code_samples',
      codeSamplesFileName:
        process.env.CODE_SAMPLES_FILE_NAME ||
        'collection.postman_collection.json',
      codeSamplesRelativePath:
        process.env.CODE_SAMPLES_RELATIVE_PATH ||
        '../cb-api-documentation/samples/cb-app/v2-pcv2',
      tocRelativePath:
        process.env.TOC_RELATIVE_PATH ||
        '../cb-api-documentation/cb-app/v2-pcv2/TOC.yaml',

      // Next.js Configuration
      basePath: process.env.NEXT_PUBLIC_BASE_PATH || '',

      // Analytics & Tracking
      gtmId: process.env.GTM_ID || null,
      ga4MeasurementId: process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID || null,

      // reCAPTCHA Configuration
      // If RECAPTCHA_ENABLED env var is set, use it; otherwise default to true
      recaptchaEnabled: process.env.RECAPTCHA_ENABLED
        ? process.env.RECAPTCHA_ENABLED === 'true'
        : true,
      recaptchaPublicKey: process.env.RECAPTCHA_SITE_KEY || null,

      // Sentry Configuration
      sentryEnabled: process.env.SENTRY_ENABLED === 'true',
      sentryDsn: process.env.NEXT_PUBLIC_SENTRY_DSN || null,
    };

    // Validate the configuration
    const validatedConfig = configSchema.parse(config);

    return validatedConfig;
  } catch (error) {
    // Provide helpful error information
    console.error('Client configuration validation failed:', error);
    throw new Error(
      'Failed to load application configuration. Please check your environment setup.'
    );
  }
}

// Load configuration at module import time
const config = loadClientConfig();

/**
 * Validated and typed configuration object
 * Import this instead of using process.env directly
 */
export { config };

/**
 * Helper functions for common environment checks
 */
export const isProduction = config.nodeEnv === 'production';
export const isDevelopment = config.nodeEnv === 'development';

/**
 * Computed values based on configuration
 * These maintain the same API as the previous implementation
 */
export const computedConfig = {
  // Dynamic URL construction for production with base path
  openApiSpecUrl:
    isProduction && config.basePath
      ? `${config.basePath}${config.openApiSpecUrl}`
      : config.openApiSpecUrl,

  samplesDirectoryPath:
    isProduction && config.basePath
      ? `${config.basePath}${config.samplesDirectoryPath}`
      : config.samplesDirectoryPath,

  // Full domain URL construction
  chargebeeDomain: `${config.cbProtocol}://${config.cbDomain}`,
};

/**
 * Type export for TypeScript users
 */
export type { Config } from './schema';
